import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", key: "home", label: "Inicio" },
  { to: "/services", key: "services", label: "Servicios" },
  { to: "/methodology", key: "methodology", label: "Método" },
  { to: "/about", key: "about", label: "Nosotros" },
  { to: "/contact", key: "contact", label: "Contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/90 backdrop-blur-xl border-b border-gray-100" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <span className="text-[#0B1F3A] text-xl font-serif font-semibold tracking-tight">Medite Group</span>
          <span className="w-1.5 h-1.5 rounded-full bg-[#0B1F3A] group-hover:scale-150 transition-transform" />
        </Link>

        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors relative py-1 ${
                  isActive ? "text-[#0B1F3A]" : "text-gray-500 hover:text-[#0B1F3A]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  {isActive && (
                    <motion.span layoutId="nav-underline" className="absolute -bottom-0.5 left-0 right-0 h-px bg-[#0B1F3A]" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="hidden md:inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] text-white px-6 py-3 text-sm font-medium transition-all duration-300 hover:scale-[1.03] hover:shadow-xl"
        >
          Diagnóstico inicial <ArrowUpRight size={16} />
        </Link>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-[#0B1F3A]"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.key}`}
                  className="text-lg text-[#0B1F3A] font-serif"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-2 rounded-full bg-[#0B1F3A] text-white px-6 py-3 text-sm font-medium text-center"
              >
                Solicitar un diagnóstico inicial
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
